import { camelCase } from 'lodash';
import type { MediaTranscode } from '@frameio/graphql-client/generated';
import type { SnakeToCamelCase } from '@frameio/utils/types/utils';
import type { KnownTranscodeKeys } from '../../utils/transcodes';

/**
 * Returns a strongly typed object of MediaTranscode objects indexed by
 * their camel-cased key name. The return value includes all data from the
 * input array, although the type uses known assumed transcode key names.
 *
 * @example
 * ```
 * getMediaTranscodesByKey([
 *   { key: 'thumb', encodeStatus: '…', streamUrl: '…' },
 *   { key: 'thumb_540', encodeStatus: '…', streamUrl: '…' },
 *   { key: 'thumb_scrub', encodeStatus: '…', streamUrl: '…' },
 * ]);
 *
 * // output
 * {
 *   thumb: { encodeStatus: '…', streamUrl: '…' },
 *   thumb540: { encodeStatus: '…', streamUrl: '…' },
 *   thumbScrub: { encodeStatus: '…', streamUrl: '…' },
 * };
 * ```
 */
export function getMediaTranscodesByKey<
  T extends Partial<MediaTranscode>,
  MediaTranscodeKeys = KnownTranscodeKeys,
>(mediaTranscodes: Array<T>) {
  if (mediaTranscodes && Array.isArray(mediaTranscodes)) {
    return mediaTranscodes.reduce(
      (acc, { key, ...value }) => {
        const transformedKey = camelCase(key);
        // eslint-disable-next-line no-param-reassign
        acc[transformedKey] = value;
        return acc;
      },
      {} as Partial<{
        [K in MediaTranscodeKeys as K extends string
          ? SnakeToCamelCase<K>
          : never]: Omit<T, 'key'>;
      }>
    );
  }

  return undefined;
}
